import React, { Suspense, lazy, useEffect, useState } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import api, { unwrap } from './utils/api';
import Header from './components/Header';
import Footer from './components/Footer';
import Home from './pages/Home';
import useCustomCursor from './hooks/useCustomCursor';
import useScreenshotBlock from './hooks/useScreenshotBlock';
import Category from './pages/Category';
import Checkout from './pages/Checkout';
import CheckoutSuccess from './pages/CheckoutSuccess';
import TermsConditions from './pages/TermsConditions';
import ContactUs from './pages/ContactUs';
import EditPurchase from './pages/EditPurchase';
import DownloadFiles from './pages/DownloadFiles';
import PersonalizeCheck from './pages/PersonalizeCheck';
import GoLink from './pages/GoLink';
import NotFound from './pages/NotFound';
import MaintenanceGate from './components/MaintenanceGate';
import AnnouncementPopup from './components/AnnouncementPopup';

// The template editor and the download page carry the heavy canvas / PDF
// libraries (Fabric.js, jsPDF). They load on demand so the first page a
// visitor opens (home, category, an ad link) shows sooner; the editor is
// prefetched in the background right after, so opening a template doesn't
// wait either.
const loadEditor = () => import('./pages/Editor');
const Editor = lazy(loadEditor);
const DownloadItem = lazy(() => import('./pages/DownloadItem'));
const pageLoading = <div style={{ padding: 40, textAlign: 'center' }}>Loading…</div>;

export default function App() {
  const location = useLocation();

  useEffect(() => {
    const idle = window.requestIdleCallback || ((cb) => setTimeout(cb, 1500));
    idle(() => { loadEditor().catch(() => {}); });
  }, []);
  // Admin-controlled cursor flags. Initial state reads from localStorage so
  // repeat visitors don't flash the wrong cursor while the fetch is in
  // flight — if the admin has disabled the custom cursor, the user's next
  // page load skips installing it entirely. First-time visitors fall back
  // to the default-on behaviour.
  const [cursorSettings, setCursorSettings] = useState(() => {
    try {
      const cached = window.localStorage.getItem('itsasimcha-cursor-settings');
      if (cached) {
        const parsed = JSON.parse(cached);
        return {
          disableCustomCursor: !!parsed.disableCustomCursor,
          disableEasing: !!parsed.disableEasing,
        };
      }
    } catch (_) { /* localStorage blocked / parse failure — fall through */ }
    return { disableCustomCursor: false, disableEasing: false };
  });
  useCustomCursor(cursorSettings);

  useEffect(() => {
    let cancelled = false;
    api.get('/miscellaneous/cursorSettings')
      .then(r => {
        const v = unwrap(r) || {};
        if (cancelled) return;
        const next = {
          disableCustomCursor: !!v.disableCustomCursor,
          disableEasing: !!v.disableEasing,
        };
        setCursorSettings(next);
        try {
          window.localStorage.setItem('itsasimcha-cursor-settings', JSON.stringify(next));
        } catch (_) { /* private mode / quota — silently skip caching */ }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [location.key]);

  // Admin-controlled flag. Default to true so we never briefly expose
  // dev tools while the setting is still loading. The same flag also
  // gates the screenshot block — when admin allows dev tools (for
  // debugging the live site), screenshots are allowed too.
  const [blockDevTools, setBlockDevTools] = useState(true);
  useScreenshotBlock(blockDevTools);

  useEffect(() => {
    let cancelled = false;
    async function fetchFlag() {
      try {
        const r = await api.get('/miscellaneous/devToolsBlock');
        const v = unwrap(r);
        if (cancelled) return;
        setBlockDevTools(v?.enabled !== false);
      } catch (_) {
        if (cancelled) return;
        setBlockDevTools(true);
      }
    }
    fetchFlag();
    const t = setInterval(fetchFlag, 60_000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  useEffect(() => {
    if (!blockDevTools) return undefined;

    const handleContextMenu = (e) => {
      e.preventDefault();
      return false;
    };

    const handleKeyDown = (e) => {
      const k = (e.key || '').toLowerCase();
      const ctrlOrMeta = e.ctrlKey || e.metaKey;
      if (
        e.key === 'F12' ||
        (ctrlOrMeta && e.shiftKey && (k === 'i' || k === 'j' || k === 'c')) ||
        (ctrlOrMeta && e.altKey && (k === 'i' || k === 'j' || k === 'c')) ||
        (ctrlOrMeta && k === 'u')
      ) {
        e.preventDefault();
        return false;
      }
    };

    document.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [blockDevTools]);

  return (
    <MaintenanceGate>
      {/* Full-height column: the footer sits at the bottom of the window even
          when a page is shorter than the screen. */}
      <div className="site-shell">
      <Header />
      <div className="site-main">
      <Suspense fallback={pageLoading}>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/categories/:slug" element={<Category />} />
        <Route path="/categories/:slug/:subSlug" element={<Category />} />
        <Route path="/card/:productId" element={<Editor />} />
        <Route path="/checkout" element={<Checkout />} />
        <Route path="/checkout/success" element={<CheckoutSuccess />} />
        <Route path="/download/:orderId/:itemId" element={<DownloadItem />} />
        <Route path="/contact-us" element={<ContactUs />} />
        <Route path="/terms-conditions" element={<TermsConditions />} />
        <Route path="/edit-purchase" element={<EditPurchase />} />
        <Route path="/download-files" element={<DownloadFiles />} />
        <Route path="/personalize-check" element={<PersonalizeCheck />} />
        <Route path="/go/:slug" element={<GoLink />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
      </Suspense>
      </div>
      <Footer />
      </div>
      <AnnouncementPopup />
    </MaintenanceGate>
  );
}
